import { FC, useContext, createContext, ReactNode, useMemo } from 'react';
import { ThemeProvider as DSThemeProvider } from '@robocorp/theme';
import { InterfaceTheme } from '@robocorp/robocloud-internal-cds-sdk/lib/src/types/RcInstanceDB';
import { useSystemTheme, ViewportProvider } from '@robocorp/components';
import { LocalStorageItemKeys, useLocalStorage } from './useStorage';

type ThemeContextType = {
  theme: InterfaceTheme;
  setTheme: (theme: InterfaceTheme) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  theme: InterfaceTheme.system,
  setTheme: () => null,
});

const useTheme = () => {
  const context = useContext(ThemeContext);
  return context;
};

export const ThemeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const systemTheme = useSystemTheme();

  const { storageValue: theme, setStorageValue: setTheme } = useLocalStorage({
    key: LocalStorageItemKeys.THEME,
    initialValue: InterfaceTheme.system,
    sync: true,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const context = useMemo(() => ({ theme, setTheme }), [theme]);

  return (
    <ThemeContext.Provider value={context}>
      <DSThemeProvider name={theme === InterfaceTheme.system ? systemTheme : theme}>
        <ViewportProvider>{children}</ViewportProvider>
      </DSThemeProvider>
    </ThemeContext.Provider>
  );
};

export default useTheme;
